/**
 * Which methods ask for criteria — the one shared answer.
 *
 * Kept free of store imports so pure logic (the Event Designer's step
 * matrix) and the session wizard can both read it.
 */
import { REGISTRY_TOOL_SPECS_BY_ID } from '../generated/registryToolSpecs';

/**
 * Maps registry tool IDs to the attribute keys their algorithm mandates.
 *
 * When a tool from this map is selected in the session wizard the matching
 * workspace criteria are auto-selected on the Criteria step so the user
 * doesn't have to hunt for them manually.
 *
 * Rules for inclusion:
 *  - List ONLY tools whose dimensions are fixed by the algorithm (RICE = R/I/C/E, etc.).
 *  - Omit tools with user-defined criteria (simple_weighted_scoring, ahp) — the user
 *    must pick freely.
 *  - Omit tools that don't use a criteria step at all (stack_ranking, pairwise, etc.).
 *
 * Keys match the `attributeKey` field on Criteria rows in Supabase.
 */
export const TOOL_REQUIRED_CRITERIA: Record<string, string[]> = {
  rice:                 ['reach', 'impact', 'confidence', 'effort'],
  wsjf:                 ['cost_of_delay', 'job_duration'],
  cost_of_delay:        ['value_lost', 'cost_to_restart'],
  impact_effort_matrix: ['impact', 'effort'],
  pareto_analysis:      ['contribution'],
};

/**
 * Does this method ask the facilitator for criteria (S3) and values (S4)?
 *
 * One answer for every surface that sets up a session — the session wizard
 * and the Event Designer both read it. True only for methods that score items
 * against criteria the facilitator chooses (simple weighted scoring). Methods
 * with their own built-in dimensions — RICE, WSJF, Cost of Delay,
 * Impact/Effort — collect those inside the tool, so asking for criteria first
 * is a step with nothing to decide. Methods that use no criteria at all
 * (stack ranking, pairwise, dot voting…) are false too.
 */
export function toolAsksForCriteria(toolId?: string | null): boolean {
  // Registry ids (rice, wsjf…). Callers holding a UI alias normalize first
  // (normalizeSessionToolId in ./sessionToolCatalog).
  const canonical = toolId ?? '';
  if (!canonical) return false;
  const spec = REGISTRY_TOOL_SPECS_BY_ID[canonical];
  if (!spec?.compatibility.requiresCriteria) return false;
  if (spec.compatibility.toolDefinesCriteria) return false;
  if ((TOOL_REQUIRED_CRITERIA[canonical] ?? []).length > 0) return false;
  return true;
}
